import { Heart, Loader2 } from 'lucide-react'

import { Button } from '../../Theme'
import { LABELS } from '../../UiBlocks'
import { useWishlistState } from '../state'

interface AddToWishlistButtonProps {
  /** Product SKU */
  sku: string
  /** Disabled state */
  disabled?: boolean
  /** Custom className */
  className?: string
  /** Show text label (default: false for icon-only) */
  showLabel?: boolean
}

/**
 * AddToWishlistButton - Button component for adding/removing products from wishlist
 *
 * Uses WishlistProvider context for wishlist operations.
 * Shows loading spinner when processing, filled heart when in wishlist, outline heart when not.
 * Icon-only by default (compact), with optional text label.
 *
 * @example
 * // Inside a component wrapped with WishlistProvider
 * <AddToWishlistButton sku="SKU123" />
 */
export function AddToWishlistButton({
  sku,
  disabled = false,
  className,
  showLabel = false,
}: AddToWishlistButtonProps) {
  const { handleToggleWishlist, isInWishlist, processingWishlist } = useWishlistState()

  const isLoading = processingWishlist === sku
  const inWishlist = isInWishlist(sku)

  const handleClick = () => {
    if (!isLoading && !disabled) {
      handleToggleWishlist(sku)
    }
  }

  const label = inWishlist ? LABELS.REMOVE_FROM_WISHLIST : LABELS.ADD_TO_WISHLIST
  const ariaLabel = inWishlist ? 'Remove from wishlist' : 'Add to wishlist'

  return (
    <Button
      variant="wishlist"
      size="wishlist"
      onClick={handleClick}
      disabled={isLoading || disabled}
      className={className}
      aria-label={ariaLabel}
      title={label}
    >
      {isLoading ? (
        <Loader2 className="animate-spin w-6 h-6" aria-hidden="true" />
      ) : (
        <>
          {showLabel && (
            <span className="font-sec-font text-sm md:text-base mr-2 group-hover/wishlist:text-primary transition-colors duration-300">
              {label}
            </span>
          )}
          <span className={inWishlist ? 'text-button-color' : 'text-main-color group-hover/wishlist:text-button-color transition-colors duration-300'}>
            <Heart size={22} fill="currentColor" strokeWidth={0} aria-hidden="true" />
          </span>
        </>
      )}
    </Button>
  )
}
